/**
 * O app como aplicativo instalável.
 *
 * O service worker (`sw.js`, gerado por `scripts/pos-build.ts`) guarda o build inteiro
 * em cache no primeiro acesso. Como o app não faz requisição nenhuma em tempo de
 * execução, isso basta para ele abrir sem internet — numa escola sem rede, no celular
 * em modo avião, num auditório com Wi-Fi ruim.
 *
 * Duas precauções que existem por causa de demonstração ao vivo:
 *
 *  - **Só em HTTPS.** Em `file://` (o arquivo único do pendrive) não há service worker,
 *    e tentar registrar só geraria erro no console.
 *  - **A escotilha `?nosw=1`**, que desregistra tudo e limpa os caches. Se a página
 *    abrir com uma versão velha na hora errada, é o que se digita.
 */

/** Registra o service worker, ou o desliga se o endereço trouxer `?nosw=1`. */
export function registrarServiceWorker(avisar: (texto: string) => void): void {
  if (!('serviceWorker' in navigator)) return;

  if (new URLSearchParams(location.search).has('nosw')) {
    void desligar(avisar);
    return;
  }

  if (location.protocol !== 'https:' && location.hostname !== 'localhost') return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('./sw.js')
      .then((reg) => {
        reg.addEventListener('updatefound', () => {
          const novo = reg.installing;
          if (!novo) return;
          novo.addEventListener('statechange', () => {
            // Só avisa quem já estava com uma versão na mão: na primeira visita, a
            // instalação é silenciosa.
            if (novo.state === 'installed' && navigator.serviceWorker.controller) {
              avisar('Há uma versão nova. Recarregue a página para usá-la.');
            }
          });
        });
      })
      .catch(() => {
        // Sem service worker o app continua inteiro; só não abre offline.
      });
  });
}

async function desligar(avisar: (texto: string) => void): Promise<void> {
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
    if ('caches' in window) {
      const chaves = await caches.keys();
      await Promise.all(chaves.map((k) => caches.delete(k)));
    }
    avisar('Cache do aplicativo limpo. Recarregue sem "?nosw=1" para voltar ao normal.');
  } catch {
    avisar('Não foi possível limpar o cache neste navegador.');
  }
}
